import type { ServicePageData } from "$lib/types/service-detail";

const jewelryRetouchingMedia = {
  heroJewelry: {
    src: "/images/jewelry-retouching/jewelry-retouching-hero-emerald-halo-water-splash.webp",
    alt: "Emerald halo diamond ring on a bright white surface beside blue water",
    width: 1600,
    height: 2000,
  },
  heroRing: {
    src: "/images/jewelry-retouching/jewelry-retouching-hero-diamond-halo-ring-set.webp",
    alt: "Fine jewelry diamond halo ring set with precision micro-prongs and sparkling facets",
    width: 1600,
    height: 2000,
  },
  heroPendant: {
    src: "/images/jewelry-retouching/jewelry-retouching-hero-delicate-gold-pendant.webp",
    alt: "Delicate gold pendant necklace photographed with clean specular reflections and delicate gradient shadows",
    width: 1600,
    height: 2000,
  },
  introDiamond: {
    src: "/images/jewelry-retouching/jewelry-retouching-intro-gold-diamond-comparison-board.webp",
    alt: "Before and after jewelry retouching examples for diamond rings, a pendant, earrings, and bracelet",
    width: 1600,
    height: 2000,
  },
  comparisonBandBefore: {
    src: "/images/jewelry-retouching/jewelry-retouching-comparison-oval-diamond-band-before.webp",
    alt: "Gold and diamond eternity band before retouching, with a marked studio background",
    width: 1596,
    height: 1995,
  },
  comparisonBandAfter: {
    src: "/images/jewelry-retouching/jewelry-retouching-comparison-oval-diamond-band-after.webp",
    alt: "Gold and diamond eternity band after cleanup and jewelry retouching",
    width: 1596,
    height: 1995,
  },
  comparisonSapphireBefore: {
    src: "/images/jewelry-retouching/jewelry-retouching-showcase-pink-sapphire-halo-before.webp",
    alt: "Pink sapphire halo ring before high-end jewelry retouching",
    width: 1598,
    height: 1997,
  },
  comparisonSapphireAfter: {
    src: "/images/jewelry-retouching/jewelry-retouching-showcase-pink-sapphire-halo-after.webp",
    alt: "Pink sapphire halo ring after high-end jewelry retouching",
    width: 1598,
    height: 1997,
  },
  necklacePendantBefore: {
    src: "/images/jewelry-retouching/jewelry-gold-necklace-pendant-retouch-before.webp",
    alt: "Gold pendant necklace raw capture with dust, surface imperfections, and studio glare before retouching",
    width: 1600,
    height: 2000,
  },
  necklacePendantAfter: {
    src: "/images/jewelry-retouching/jewelry-gold-necklace-pendant-retouch-after.webp",
    alt: "Finished gold pendant necklace with polished precious metal sheen, facet clarity, and specular reflection control",
    width: 1600,
    height: 2000,
  },
} as const;

export const jewelryRetouchingPage: ServicePageData = {
  slug: "jewelry-retouching",
  seo: {
    title: "Jewelry Photo Retouching | Studio Click House",
    description:
      "Specialized jewelry photo retouching by Studio Click House. Macro diamond sparkle, gemstone clarity, gold & platinum polishing, focus stacking alignment, and reflection cleanup for luxury brands.",
  },
  hero: {
    title: "Jewelry",
    titleAccent: "Retouching.",
    theme: "light",
    mediaFit: "cover",
    aspectRatio: "4/5",
    description:
      "Macro retouching for fine jewelry, luxury watches, and gemstones. We remove dust, polish metals, control reflections, and enhance diamond sparkle without losing natural detail.",
    media: jewelryRetouchingMedia.heroJewelry,
    supportingMedia: [
      jewelryRetouchingMedia.heroRing,
      jewelryRetouchingMedia.heroPendant,
    ],
  },
  intro: {
    heading: "What is high-precision jewelry retouching?",
    paragraphs: [
      "Jewelry is the most technically challenging subject in commercial photography. Macro lenses reveal microscopic dust, scratches, camera reflections, and depth-of-field blur that can diminish the perceived value of a luxury piece. Our specialist jewelry artists clean and polish precious metals (yellow gold, white gold, rose gold, platinum), align focus stacks, enhance gemstone clarity, and balance light dispersion to make every diamond and gemstone gleam with brilliance.",
    ],
    stages: [
      {
        label: "Gemstone Clarity & Facet Polish",
        description:
          "We clean internal cloudiness, sharpen facet junctions, and balance light refraction across diamonds and precious stones.",
        media: jewelryRetouchingMedia.introDiamond,
      },
      {
        label: "Precious Metal Smoothing & Tone",
        description:
          "Scratches and porous metal textures are smoothed while preserving natural brushed and mirror reflection lines.",
      },
      {
        label: "Focus Stacking & Sparkle Control",
        description:
          "We blend multi-exposure focus stacks and add subtle, realistic sparkle highlights to make prongs and stones pop.",
      },
    ],
  },
  beforeAfter: {
    heading: "Diamond eternity band retouching",
    description:
      "Compare the original eternity band with its clean catalog finish, refining the diamonds and gold while keeping the setting true to the piece.",
    bullets: [
      "Remove dust and distracting background marks",
      "Refine diamond clarity and setting detail",
      "Preserve natural gold tone and metal reflections",
    ],
    beforeSrc: jewelryRetouchingMedia.comparisonBandBefore.src,
    beforeAlt: jewelryRetouchingMedia.comparisonBandBefore.alt,
    afterSrc: jewelryRetouchingMedia.comparisonBandAfter.src,
    afterAlt: jewelryRetouchingMedia.comparisonBandAfter.alt,
    beforeLabel: "Before",
    afterLabel: "After",
    width: jewelryRetouchingMedia.comparisonBandAfter.width,
    height: jewelryRetouchingMedia.comparisonBandAfter.height,
    layout: "cards",
    textPosition: "right",
  },
  showcase: {
    heading: "High-end jewelry retouching",
    theme: "light",
    description:
      "Refine a pink sapphire and platinum halo ring with careful facet cleanup, balanced reflections, and natural gemstone color.",
    bullets: [
      "Clean dust and surface distractions",
      "Refine sapphire facets and surrounding diamond detail",
      "Polish platinum reflections while preserving natural tone",
    ],
    beforeAfter: {
      before: { ...jewelryRetouchingMedia.comparisonSapphireBefore, label: "Before" },
      after: { ...jewelryRetouchingMedia.comparisonSapphireAfter, label: "After" },
    },
  },
  additionalBeforeAfter: [
    {
      sectionId: "jewelry-retouching-gold-pendant-comparison",
      heading: "High-end jewelry & gold pendant retouching",
      description:
        "Polish delicate gold chains, refine intricate pendant settings, eliminate micro-scratches and dust, and sculpt realistic metallic reflections for luxury catalog presentations.",
      bullets: [
        "Polish fine gold chain links and eliminate micro-casting flaws",
        "Refine specular highlights and natural metallic curvature",
        "Deliver pristine, high-resolution luxury catalog packshots",
      ],
      beforeSrc: jewelryRetouchingMedia.necklacePendantBefore.src,
      beforeAlt: jewelryRetouchingMedia.necklacePendantBefore.alt,
      afterSrc: jewelryRetouchingMedia.necklacePendantAfter.src,
      afterAlt: jewelryRetouchingMedia.necklacePendantAfter.alt,
      beforeLabel: "Before",
      afterLabel: "After",
      width: jewelryRetouchingMedia.necklacePendantAfter.width,
      height: jewelryRetouchingMedia.necklacePendantAfter.height,
      layout: "cards",
      textPosition: "right",
    },
  ],
  productTypes: [
    {
      id: "gold-stud-earrings",
      code: "01",
      title: "Gold Stud Earrings",
      subtitle: "Polish gold settings and bring out clear, balanced stone detail.",
      src: "/images/jewelry-retouching/jewelry-retouching-product-gold-stud-earrings.webp",
      alt: "Gold stud earrings retouched for a jewelry catalog",
      fit: "cover",
    },
    {
      id: "brilliant-solitaire-ring",
      code: "02",
      title: "Brilliant Solitaire Ring",
      subtitle: "Refine diamond facets and keep the solitaire setting crisp.",
      src: "/images/jewelry-retouching/jewelry-retouching-product-solitaire-diamond-ring.webp",
      alt: "Round brilliant solitaire diamond ring in a studio product image",
      fit: "cover",
    },
    {
      id: "aquamarine-emerald-ring",
      code: "03",
      title: "Aquamarine & Emerald Ring",
      subtitle: "Balance color and reflections across mixed gemstones and gold.",
      src: "/images/jewelry-retouching/jewelry-retouching-product-aquamarine-emerald-ring.webp",
      alt: "Gold ring with aquamarine and emerald gemstones",
      fit: "cover",
    },
    {
      id: "rose-gold-halo-ring",
      code: "04",
      title: "Rose Gold Halo Ring",
      subtitle: "Show the twisted band and halo setting with natural metal tone.",
      src: "/images/jewelry-retouching/jewelry-retouching-product-rose-gold-halo-twisted-band.webp",
      alt: "Rose gold solitaire halo ring with a twisted band",
      fit: "cover",
    },
    {
      id: "diamond-eternity-band",
      code: "05",
      title: "Diamond Eternity Band",
      subtitle: "Keep each stone and setting defined in a vivid campaign image.",
      src: "/images/jewelry-retouching/jewelry-retouching-product-diamond-eternity-water.webp",
      alt: "Diamond eternity band photographed against a blue water setting",
      fit: "cover",
    },
    {
      id: "rose-gold-chevron-bridal-set",
      code: "06",
      title: "Rose Gold Chevron Bridal Set",
      subtitle: "Present the curved bridal bands with clean edges and detail.",
      src: "/images/jewelry-retouching/jewelry-retouching-product-rose-gold-chevron-bridal-set.webp",
      alt: "Rose gold diamond curved chevron bridal ring set",
      fit: "cover",
    },
  ],
  features: {
    heading: "Jewelry photo retouching techniques",
    items: [
      {
        icon: "Gem",
        title: "Gemstone Facet Sharpening",
        description:
          "We sharpen internal facet edges and balance spectral light dispersion to make stones look radiant and deep.",
      },
      {
        icon: "Sparkles",
        title: "Precious Metal Smoothing",
        description:
          "We eliminate casting pits, micro-scratches, and solder marks while preserving realistic brushed and high-polish reflections.",
      },
      {
        icon: "Focus",
        title: "Focus Stack Alignment",
        description:
          "We blend multi-bracket focus shots for edge-to-edge sharpness from front prongs to rear shank.",
      },
      {
        icon: "Sun",
        title: "Reflection & Glare Control",
        description:
          "We remove camera reflections, studio softbox glare, and dark shadows while maintaining natural metallic curvature.",
      },
      {
        icon: "Palette",
        title: "True Metal Color Calibration",
        description:
          "Accurate differentiation between 14k/18k yellow gold, rose gold, white gold, platinum, and sterling silver.",
      },
      {
        icon: "ShieldCheck",
        title: "Sub-Pixel Detail Inspection",
        description:
          "Senior jewelry directors inspect every file at 500% zoom before delivery to guarantee museum-grade polish.",
      },
    ],
  },
  faqs: [
    {
      question: "How do you handle macro lens focus stacking?",
      answer:
        "We accept multi-frame focus brackets. Our team aligns and blends the images to create a composite with infinite depth of field, ensuring that the front stone, side prongs, and rear shank are all tack-sharp.",
    },
    {
      question: "Can you change the metal color (e.g., from yellow gold to rose gold or platinum)?",
      answer:
        "Yes. We can recolor metal components to showcase all available metal variants (yellow gold, white gold, rose gold, platinum) from a single photoshoot, saving significant production costs.",
    },
    {
      question: "Do you retain the natural color of colored gemstones?",
      answer:
        "Yes, we carefully protect the authentic hue and saturation of rubies, emeralds, sapphires, and opals, balancing internal light without distorting true gemological characteristics.",
    },
    {
      question: "What is the turnaround time for luxury jewelry retouching?",
      answer:
        "Jewelry retouching requires high artistry. Batches of 20 to 50 items are typically delivered in 24 to 48 hours. Express 24-hour delivery is available for urgent launches.",
    },
    {
      question: "Can I test your jewelry retouching quality on a sample file?",
      answer:
        "Yes, send us 1 to 2 RAW jewelry or watch files, and we will deliver a free test edit showcasing our diamond and metal finish.",
    },
  ],
  cta: {
    heading: "Make your jewelry collection sparkle",
    description:
      "Send us your macro jewelry shots. Our senior jewelry artists will deliver a complimentary test edit and custom quote.",
    steps: [
      {
        title: "Submit macro RAW files",
        description:
          "Upload your jewelry photos or focus-stacked brackets with your styling notes.",
      },
      {
        title: "Inspect test brilliance",
        description:
          "Examine our metal smoothing and gemstone clarity at 500% zoom with zero obligation.",
      },
      {
        title: "Launch luxury catalog",
        description:
          "Receive high-resolution, print-ready and e-commerce formatted deliverables.",
      },
    ],
  },
};
